import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Settings, 
  BarChart3, 
  Target, 
  Sliders, 
  Edit3,
  Download,
  Printer,
  Link,
  Save,
  X,
  Clock,
  ChevronDown,
  ChevronRight,
  Pencil,
  Check,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  ShoppingCart
} from 'lucide-react';
import BuyingPlanTab from './BuyingPlanTab';

/**
 * Helper function to shorten long product names for grid display
 * Takes the text before first " - " and removes brand name
 * @param {string} fullTitle - Full product title
 * @returns {string} Shortened product name
 */
const getShortProductName = (fullTitle) => {
  if (!fullTitle) return '';
  
  // Take text before first " - "
  const beforeDash = fullTitle.split(' - ')[0];
  
  // Remove common brand names
  const withoutBrand = beforeDash
    .replace(/^PetYupp\s+/i, '')
    .replace(/^ZuperTools\s+/i, '')
    .trim();
  
  // Limit to 50 chars
  return withoutBrand.length > 50 
    ? withoutBrand.substring(0, 47) + '...'
    : withoutBrand;
};

const ForecastTab = () => {
  const [activeView, setActiveView] = useState('summary');
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadForecast();
  }, []);

  const loadForecast = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/v1/forecast/2026?tenant_id=tenant-123');
      setForecastData(data);
      setError(null);
    } catch (err) {
      console.error('Error loading forecast:', err);
      setError(err.message || 'Forecast not found. Please generate forecast first.');
    } finally {
      setLoading(false);
    }
  };

  const generateForecast = async () => {
    try {
      setGenerating(true);
      await api.post('/api/v1/forecast/generate?year=2026&tenant_id=tenant-123&growth_rate=1.20');
      await loadForecast();
    } catch (err) {
      console.error('Error generating forecast:', err);
      setError('Failed to generate forecast: ' + (err.message || 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-xs text-gray-400">Loading forecast...</p>
        </div>
      </div>
    );
  }

  if (error || !forecastData) {
    return (
      <div className="bg-slate-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-white mb-2">📈 2026 Sales Forecast</h3>
        <div className="bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-3 mb-3">
          <p className="text-xs text-yellow-400 mb-2">{error || 'No forecast data available'}</p>
          <p className="text-[10px] text-gray-400">Generate a forecast based on 2025 Veeqo order data with 20% growth rate.</p>
        </div>
        <button 
          onClick={generateForecast}
          disabled={generating}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
        >
          {generating ? '⏳ Generating...' : '🚀 Generate 2026 Forecast'}
        </button>
      </div>
    );
  }

  // Handle empty forecast (no data)
  if (forecastData && forecastData.summary && forecastData.summary.total_products === 0) {
    return (
      <div className="bg-slate-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-white mb-2">📈 2026 Sales Forecast</h3>
        <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-3 mb-3">
          <p className="text-xs text-blue-400 mb-2">No 2025 order data available for forecast</p>
          <p className="text-[10px] text-gray-400">
            To generate a sales forecast, you need to sync your Veeqo orders first. 
            Once 2025 order data is available, the system will calculate a 2026 forecast with 20% growth.
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
          >
            🔄 Refresh
          </button>
          <a 
            href="/settings"
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
          >
            ⚙️ Go to Settings to Sync Veeqo
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* View Tabs */}
      <div className="flex gap-1 mb-3 border-b border-slate-700">
        <button 
          onClick={() => setActiveView('summary')}
          className={`px-3 py-1.5 text-xs font-medium border-b-2 ${activeView === 'summary' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-white'}`}
        >
          📊 Summary
        </button>
        <button 
          onClick={() => setActiveView('products')}
          className={`px-3 py-1.5 text-xs font-medium border-b-2 ${activeView === 'products' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-white'}`}
        >
          📦 By Product
        </button>
        <button 
          onClick={() => setActiveView('manage')}
          className={`px-3 py-1.5 text-xs font-medium border-b-2 ${activeView === 'manage' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-white'}`}
        >
          ⚙️ Manage
        </button>
        <button 
          onClick={() => setActiveView('buying')}
          className={`px-3 py-1.5 text-xs font-medium border-b-2 flex items-center gap-1 ${activeView === 'buying' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-white'}`}
          data-testid="buying-plan-tab"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          Buying Plan
        </button>
        <div className="flex-1"></div>
        <button 
          onClick={loadForecast}
          disabled={loading}
          className="px-2 py-1 text-[10px] text-gray-400 hover:text-white disabled:opacity-50"
        >
          🔄 Refresh
        </button>
      </div>

      {activeView === 'summary' && <SummaryView forecast={forecastData} />}
      {activeView === 'products' && <ProductsView products={forecastData.products} onForecastUpdate={loadForecast} />}
      {activeView === 'manage' && <ManageView forecast={forecastData} onUpdate={loadForecast} />}
      {activeView === 'buying' && <BuyingPlanTab tenantId="tenant-123" />}
    </div>
  );
};

// Summary View Component
const SummaryView = ({ forecast }) => {
  const summary = forecast.summary;
  const ytdActual = summary.ytd_actual || 0;
  const progress = summary.annual_target > 0 ? (ytdActual / summary.annual_target) * 100 : 0;
  const remaining = summary.annual_target - ytdActual;
  
  const currentMonth = new Date().getMonth() + 1;
  const expectedPace = currentMonth / 12;
  const actualPace = summary.annual_target > 0 ? ytdActual / summary.annual_target : 0;
  const paceRatio = expectedPace > 0 ? actualPace / expectedPace : 1;
  
  let status = { label: "On Track", color: "green", icon: "✓" };
  if (paceRatio < 0.75) {
    status = { label: "Behind", color: "red", icon: "🚨" };
  } else if (paceRatio < 0.95) {
    status = { label: "Slightly Behind", color: "yellow", icon: "⚠️" };
  } else if (paceRatio > 1.15) {
    status = { label: "Ahead", color: "green", icon: "🚀" };
  }

  // Calculate monthly aggregates
  const monthlyData = [];
  for (let month = 1; month <= 12; month++) {
    let forecast2025 = 0;
    let forecast2026 = 0;
    let actual2026 = 0;
    
    (forecast.products || []).forEach(product => {
      forecast2025 += product.actual_2025?.monthly?.[month]?.revenue || 0;
      forecast2026 += product.forecast_2026?.monthly?.[month]?.revenue || 0;
      actual2026 += product.actual_2026?.monthly?.[month]?.revenue || 0;
    });
    
    monthlyData.push({
      month,
      forecast2025,
      forecast2026,
      actual2026,
      variance: actual2026 - forecast2026
    });
  }

  return (
    <div>
      {/* Summary Card */}
      <div className="bg-slate-800 rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-white">📈 2026 Sales Forecast</h3>
          <span className={`text-xs font-semibold ${status.color === 'green' ? 'text-green-400' : status.color === 'yellow' ? 'text-yellow-400' : 'text-red-400'}`}>
            {status.icon} {status.label}
          </span>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
          <div className="bg-slate-900 rounded p-2">
            <p className="text-[10px] uppercase text-gray-400 mb-0.5">Annual Target</p>
            <p className="text-lg font-bold text-white">${((Number(summary.annual_target) || 0) / 1000).toFixed(0)}K</p>
          </div>
          <div className="bg-slate-900 rounded p-2">
            <p className="text-[10px] uppercase text-gray-400 mb-0.5">YTD Actual</p>
            <p className="text-lg font-bold text-green-400">${((Number(ytdActual) || 0) / 1000).toFixed(1)}K</p>
          </div>
          <div className="bg-slate-900 rounded p-2">
            <p className="text-[10px] uppercase text-gray-400 mb-0.5">Remaining</p>
            <p className="text-lg font-bold text-blue-400">${((Number(remaining) || 0) / 1000).toFixed(0)}K</p>
          </div>
          <div className="bg-slate-900 rounded p-2">
            <p className="text-[10px] uppercase text-gray-400 mb-0.5">Progress</p>
            <p className="text-lg font-bold text-white">{(Number(progress) || 0).toFixed(1)}%</p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="relative h-5 bg-slate-900 rounded-lg overflow-hidden">
          <div 
            className={`absolute h-full ${status.color === 'green' ? 'bg-gradient-to-r from-blue-600 to-green-500' : status.color === 'yellow' ? 'bg-gradient-to-r from-yellow-600 to-orange-500' : 'bg-gradient-to-r from-red-600 to-orange-500'}`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white">
            ${((Number(ytdActual) || 0) / 1000).toFixed(0)}K / ${((Number(summary.annual_target) || 0) / 1000).toFixed(0)}K
          </div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="p-2 border-b border-slate-700">
          <h3 className="text-xs font-semibold text-white">Monthly Performance</h3>
        </div>
        
        <table className="w-full">
          <thead className="bg-slate-900">
            <tr>
              <th className="px-2 py-1.5 text-left text-[10px] uppercase text-gray-400">MONTH</th>
              <th className="px-2 py-1.5 text-right text-[10px] uppercase text-gray-400">2025 ACTUAL</th>
              <th className="px-2 py-1.5 text-right text-[10px] uppercase text-gray-400">2026 FORECAST</th>
              <th className="px-2 py-1.5 text-right text-[10px] uppercase text-gray-400">2026 ACTUAL</th>
              <th className="px-2 py-1.5 text-right text-[10px] uppercase text-gray-400">VARIANCE</th>
              <th className="px-2 py-1.5 text-center text-[10px] uppercase text-gray-400">STATUS</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData.map((data) => {
              const monthName = new Date(2026, data.month - 1).toLocaleString('default', { month: 'short' });
              const achievement = data.actual2026 > 0 ? (data.actual2026 / data.forecast2026) * 100 : null;
              
              return (
                <tr key={data.month} className="border-b border-slate-700 hover:bg-slate-700/50">
                  <td className="px-2 py-1.5 text-xs text-white font-semibold">{monthName}</td>
                  <td className="px-2 py-1.5 text-xs text-gray-400 text-right">
                    ${((Number(data.forecast2025) || 0) / 1000).toFixed(1)}K
                  </td>
                  <td className="px-2 py-1.5 text-xs text-blue-400 text-right font-semibold">
                    ${((Number(data.forecast2026) || 0) / 1000).toFixed(1)}K
                  </td>
                  <td className="px-2 py-1.5 text-xs text-white text-right font-semibold">
                    {data.actual2026 > 0 ? `$${(data.actual2026 / 1000).toFixed(1)}K` : '-'}
                  </td>
                  <td className={`px-2 py-1.5 text-xs text-right font-semibold ${
                    data.variance > 0 ? 'text-green-400' : data.variance < 0 ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    {data.actual2026 > 0 ? (data.variance >= 0 ? '+' : '') + `$${(data.variance / 1000).toFixed(1)}K` : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {achievement !== null ? (
                      <span className={`text-[10px] font-semibold ${
                        achievement >= 100 ? 'text-green-400' :
                        achievement >= 75 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {achievement >= 100 ? '✓' : '⚠️'} {achievement.toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-500">Pending</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Products View Component - REDESIGNED with expanded table and inline editing
const ProductsView = ({ products, onForecastUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSKU, setExpandedSKU] = useState(null);
  const [editedForecasts, setEditedForecasts] = useState({}); // Track edits per SKU
  const [editingCell, setEditingCell] = useState(null); // { sku, month, field }
  const [saving, setSaving] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingCollapseSKU, setPendingCollapseSKU] = useState(null);
  const inputRef = useRef(null);
  
  const filteredProducts = useMemo(() => 
    (products || []).filter(p => 
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.product_title?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [products, searchTerm]
  );
  
  // Month names
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Format currency
  const formatCurrency = (value, compact = false) => {
    if (value === null || value === undefined) return '-';
    if (compact) {
      return value >= 1000 ? `$${(value / 1000).toFixed(1)}K` : `$${value.toFixed(0)}`;
    }
    return `$${value.toFixed(2)}`;
  };
  
  // Format variance with sign
  const formatVariance = (value, compact = false) => {
    if (value === null || value === undefined) return '-';
    const sign = value >= 0 ? '+' : '';
    if (compact) {
      return value >= 1000 || value <= -1000 
        ? `${sign}$${(value / 1000).toFixed(1)}K` 
        : `${sign}$${value.toFixed(0)}`;
    }
    return `${sign}$${value.toFixed(2)}`;
  };
  
  // Get price for SKU from product_master
  const getProductPrice = (product) => {
    const pm = product?.product_master || {};
    return pm.wholesale_price || pm.current_price || pm.retail_price || 0;
  };
  
  // Check if SKU has unsaved edits
  const hasUnsavedEdits = (sku) => {
    return Object.keys(editedForecasts[sku] || {}).length > 0;
  };
  
  // Handle row expand/collapse with unsaved changes check
  const handleRowClick = (sku) => {
    if (expandedSKU === sku) {
      // Trying to collapse
      if (hasUnsavedEdits(sku)) {
        setPendingCollapseSKU(sku);
        setShowUnsavedWarning(true);
      } else {
        setExpandedSKU(null);
      }
    } else {
      setExpandedSKU(sku);
    }
  };
  
  // Discard changes for a SKU
  const discardChanges = (sku) => {
    setEditedForecasts(prev => {
      const next = { ...prev };
      delete next[sku];
      return next;
    });
    setShowUnsavedWarning(false);
    if (pendingCollapseSKU) {
      setExpandedSKU(null);
      setPendingCollapseSKU(null);
    }
  };
  
  // Start editing a cell
  const startEditing = (sku, month, field, currentValue) => {
    setEditingCell({ sku, month, field, value: currentValue || 0 });
  };
  
  // Handle edit change
  const handleEditChange = (e) => {
    const value = e.target.value;
    setEditingCell(prev => ({ ...prev, value }));
  };
  
  // Confirm edit
  const confirmEdit = (product) => {
    if (!editingCell) return;
    
    const { sku, month, field, value } = editingCell;
    const numValue = parseFloat(value) || 0;
    const price = getProductPrice(product);
    
    let newUnits, newRevenue;
    
    if (field === 'units') {
      newUnits = Math.round(numValue);
      newRevenue = price > 0 ? newUnits * price : null;
    } else {
      newRevenue = numValue;
      newUnits = price > 0 ? Math.round(numValue / price) : null;
    }
    
    // Store the edit
    setEditedForecasts(prev => ({
      ...prev,
      [sku]: {
        ...prev[sku],
        [month]: {
          units: newUnits !== null ? newUnits : (prev[sku]?.[month]?.units ?? product.forecast_2026?.monthly?.[month]?.units ?? 0),
          revenue: newRevenue !== null ? newRevenue : (prev[sku]?.[month]?.revenue ?? product.forecast_2026?.monthly?.[month]?.revenue ?? 0),
          edited: true
        }
      }
    }));
    
    setEditingCell(null);
  };
  
  // Cancel edit
  const cancelEdit = () => {
    setEditingCell(null);
  };
  
  // Handle key press in edit input
  const handleKeyDown = (e, product) => {
    if (e.key === 'Enter') {
      confirmEdit(product);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };
  
  // Get effective forecast value (edited or original)
  const getEffectiveForecast = (product, month) => {
    const edited = editedForecasts[product.sku]?.[month];
    if (edited) {
      return edited;
    }
    return product.forecast_2026?.monthly?.[month] || { units: 0, revenue: 0 };
  };
  
  // Calculate totals for a product
  const calculateTotals = (product) => {
    let forecastUnits = 0, forecastRevenue = 0;
    let actualUnits = 0, actualRevenue = 0;
    
    for (let m = 1; m <= 12; m++) {
      const fcst = getEffectiveForecast(product, m);
      const actual = product.actual_2026?.monthly?.[m] || {};
      
      forecastUnits += fcst.units || 0;
      forecastRevenue += fcst.revenue || 0;
      actualUnits += actual.units || 0;
      actualRevenue += actual.revenue || 0;
    }
    
    const varianceRevenue = actualRevenue - forecastRevenue;
    const varianceUnits = actualUnits - forecastUnits;
    const variancePct = forecastRevenue > 0 ? (varianceRevenue / forecastRevenue) * 100 : null;
    
    return {
      forecastUnits, forecastRevenue,
      actualUnits, actualRevenue,
      varianceRevenue, varianceUnits, variancePct
    };
  };
  
  // Save changes for a SKU
  const saveChanges = async (sku, product) => {
    if (!hasUnsavedEdits(sku)) return;
    
    setSaving(true);
    try {
      const edits = editedForecasts[sku];
      
      // Build updated monthly data
      const updatedMonthly = {};
      for (let m = 1; m <= 12; m++) {
        const edited = edits[m];
        const original = product.forecast_2026?.monthly?.[m] || { units: 0, revenue: 0 };
        updatedMonthly[m] = edited || original;
      }
      
      // Calculate new totals
      let totalUnits = 0, totalRevenue = 0;
      Object.values(updatedMonthly).forEach(m => {
        totalUnits += m.units || 0;
        totalRevenue += m.revenue || 0;
      });
      
      // Call API to update forecast
      await api.put('/api/v1/forecast/update', {
        tenant_id: 'tenant-123',
        year: 2026,
        sku: sku,
        forecast: {
          monthly: updatedMonthly,
          total_units: totalUnits,
          total_revenue: totalRevenue
        }
      });
      
      // Clear edits for this SKU
      setEditedForecasts(prev => {
        const next = { ...prev };
        delete next[sku];
        return next;
      });
      
      // Notify parent to reload
      if (onForecastUpdate) {
        onForecastUpdate();
      }
      
      toast.success(`Forecast updated for ${sku}`);
    } catch (err) {
      console.error('Error saving forecast:', err);
      toast.error('Failed to save forecast: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };
  
  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);
  
  // Render editable cell
  const renderEditableCell = (product, month, field, value, displayValue) => {
    const isEditing = editingCell?.sku === product.sku && 
                      editingCell?.month === month && 
                      editingCell?.field === field;
    const isEdited = editedForecasts[product.sku]?.[month]?.edited;
    const price = getProductPrice(product);
    const canAutoCalc = price > 0;
    
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="number"
          value={editingCell.value}
          onChange={handleEditChange}
          onKeyDown={(e) => handleKeyDown(e, product)}
          onBlur={() => confirmEdit(product)}
          className="w-full bg-slate-700 border border-blue-500 rounded px-1.5 py-0.5 text-xs text-white text-right focus:outline-none focus:ring-1 focus:ring-blue-400"
          step={field === 'units' ? 1 : 0.01}
        />
      );
    }
    
    return (
      <div 
        className={`group flex items-center justify-end gap-1 cursor-pointer hover:bg-slate-700/50 rounded px-1 py-0.5 transition-colors ${isEdited ? 'bg-blue-900/30 border-l-2 border-blue-500' : ''}`}
        onClick={() => startEditing(product.sku, month, field, value)}
        title={!canAutoCalc && field === 'revenue' ? 'Set product price in Product Master to enable auto-calculation' : 'Click to edit'}
      >
        <span className="text-xs text-blue-400">{displayValue}</span>
        <Pencil className="w-3 h-3 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  };
  
  // Render the expanded monthly table
  const renderMonthlyTable = (product) => {
    const totals = calculateTotals(product);
    const price = getProductPrice(product);
    const hasEdits = hasUnsavedEdits(product.sku);
    
    // Calculate 2025 totals
    const actual2025 = product.actual_2025 || {};
    const total2025Revenue = actual2025.total_revenue || 0;
    const total2025Units = actual2025.total_units || 0;
    
    // Growth calculation
    const growthPct = total2025Revenue > 0 
      ? ((totals.forecastRevenue - total2025Revenue) / total2025Revenue) * 100 
      : null;
    
    return (
      <div className="bg-slate-900 rounded-lg p-3 mt-1">
        {/* Header with title and action buttons */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-semibold text-white">Monthly Breakdown — {product.sku}</h4>
            {price > 0 && (
              <p className="text-[10px] text-gray-500">Using W price: ${price.toFixed(2)} for calculations</p>
            )}
          </div>
          
          {hasEdits && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => discardChanges(product.sku)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white bg-slate-800 rounded border border-slate-700 hover:border-slate-600 transition-colors"
              >
                <X className="w-3 h-3" />
                Discard
              </button>
              <button
                onClick={() => saveChanges(product.sku, product)}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                Save Changes
              </button>
            </div>
          )}
        </div>
        
        {/* Monthly Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800/80 sticky top-0">
                <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-400 font-medium">Month</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase text-blue-400 font-medium">Forecast Qty</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase text-blue-400 font-medium">Forecast $</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase text-gray-400 font-medium">Actual Qty</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase text-gray-400 font-medium">Actual $</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase text-gray-400 font-medium">Variance $</th>
                <th className="px-2 py-2 text-right text-[10px] uppercase text-gray-400 font-medium">Var %</th>
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((monthName, idx) => {
                const month = idx + 1;
                const fcst = getEffectiveForecast(product, month);
                const actual = product.actual_2026?.monthly?.[month] || {};
                const hasActual = (actual.revenue || 0) > 0 || (actual.units || 0) > 0;
                
                const variance = hasActual ? (actual.revenue || 0) - (fcst.revenue || 0) : null;
                const variancePct = hasActual && fcst.revenue > 0 
                  ? ((variance / fcst.revenue) * 100) 
                  : null;
                
                const isEvenRow = idx % 2 === 0;
                
                return (
                  <tr key={month} className={`border-b border-slate-800 hover:bg-slate-800/50 ${isEvenRow ? 'bg-slate-800/30' : ''}`}>
                    <td className="px-2 py-1.5 text-gray-300 font-medium">{monthName}</td>
                    <td className="px-2 py-1.5 text-right">
                      {renderEditableCell(product, month, 'units', fcst.units, fcst.units || 0)}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {renderEditableCell(product, month, 'revenue', fcst.revenue, formatCurrency(fcst.revenue))}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-300">
                      {hasActual ? actual.units : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-300">
                      {hasActual ? formatCurrency(actual.revenue) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className={`px-2 py-1.5 text-right font-medium ${
                      variance === null ? 'text-gray-600' :
                      variance > 0 ? 'text-green-400' : 
                      variance < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {variance !== null ? formatVariance(variance) : '—'}
                    </td>
                    <td className={`px-2 py-1.5 text-right ${
                      variancePct === null ? 'text-gray-600' :
                      variancePct > 0 ? 'text-green-400' : 
                      variancePct < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {variancePct !== null ? `${variancePct >= 0 ? '+' : ''}${variancePct.toFixed(0)}%` : '—'}
                    </td>
                  </tr>
                );
              })}
              
              {/* TOTAL Row */}
              <tr className="bg-slate-800 border-t-2 border-slate-600 font-semibold">
                <td className="px-2 py-2 text-white">TOTAL</td>
                <td className="px-2 py-2 text-right text-blue-400">{totals.forecastUnits}</td>
                <td className="px-2 py-2 text-right text-blue-400">{formatCurrency(totals.forecastRevenue, true)}</td>
                <td className="px-2 py-2 text-right text-white">{totals.actualUnits || <span className="text-gray-600">—</span>}</td>
                <td className="px-2 py-2 text-right text-white">{totals.actualRevenue > 0 ? formatCurrency(totals.actualRevenue, true) : <span className="text-gray-600">—</span>}</td>
                <td className={`px-2 py-2 text-right ${
                  totals.actualRevenue === 0 ? 'text-gray-600' :
                  totals.varianceRevenue > 0 ? 'text-green-400' : 
                  totals.varianceRevenue < 0 ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {totals.actualRevenue > 0 ? formatVariance(totals.varianceRevenue, true) : '—'}
                </td>
                <td className={`px-2 py-2 text-right ${
                  totals.variancePct === null ? 'text-gray-600' :
                  totals.variancePct > 0 ? 'text-green-400' : 
                  totals.variancePct < 0 ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {totals.variancePct !== null ? `${totals.variancePct >= 0 ? '+' : ''}${totals.variancePct.toFixed(0)}%` : '—'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Year comparison footer */}
        <div className="mt-3 pt-2 border-t border-slate-800">
          <p className="text-[11px] text-gray-500">
            <span className="text-gray-400">2025 Actual:</span> {formatCurrency(total2025Revenue, true)} ({total2025Units} units) 
            <span className="mx-2">→</span>
            <span className="text-blue-400">2026 Forecast:</span> {formatCurrency(totals.forecastRevenue, true)} ({totals.forecastUnits} units)
            {growthPct !== null && (
              <span className={`ml-2 ${growthPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                · {growthPct >= 0 ? '+' : ''}{growthPct.toFixed(0)}% growth target
              </span>
            )}
          </p>
        </div>
      </div>
    );
  };
  
  return (
    <div>
      {/* Search */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search by SKU or product name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
        />
      </div>
      
      {/* Products Table */}
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">📦 Forecast by Product ({filteredProducts.length} SKUs)</h3>
          <div className="text-[10px] text-gray-500">Click row to expand • Click cells to edit forecast</div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="table-fixed" style={{minWidth: '1000px'}}>
            <thead className="bg-slate-900 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-medium" style={{width: '120px'}}>SKU</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-medium" style={{width: '300px', maxWidth: '300px'}}>Product</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400 font-medium" style={{width: '140px'}}>2025</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase text-blue-400 font-medium" style={{width: '140px'}}>Forecast</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400 font-medium" style={{width: '140px'}}>Actual</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400 font-medium" style={{width: '160px'}}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.slice(0, 50).map((product) => {
                const isExpanded = expandedSKU === product.sku;
                const hasEdits = hasUnsavedEdits(product.sku);
                
                // Get totals (with edits if any)
                const totals = calculateTotals(product);
                const actual2025 = product.actual_2025 || {};
                const hasActual2026 = totals.actualRevenue > 0;
                
                // Variance calculations
                const varianceRev = hasActual2026 ? totals.varianceRevenue : null;
                const varianceUnits = hasActual2026 ? totals.varianceUnits : null;
                
                return (
                  <React.Fragment key={product.sku}>
                    {/* Main Row */}
                    <tr 
                      className={`border-b border-slate-700 hover:bg-slate-700/50 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-700/30' : ''} ${hasEdits ? 'border-l-2 border-l-blue-500' : ''}`}
                      onClick={() => handleRowClick(product.sku)}
                    >
                      {/* SKU Column */}
                      <td className="px-3 py-2" style={{width: '120px'}}>
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className="text-xs text-white font-mono truncate">{product.sku?.substring(0, 12)}</span>
                          {hasEdits && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" title="Unsaved changes" />}
                        </div>
                      </td>
                      
                      {/* Product Name */}
                      <td className="px-3 py-2" style={{width: '300px', maxWidth: '300px'}}>
                        <span className="text-xs text-gray-300 truncate block" title={product.product_title}>
                          {getShortProductName(product.product_title)}
                        </span>
                      </td>
                      
                      {/* 2025 Column - Stacked */}
                      <td className="px-3 py-2 text-right" style={{width: '140px'}}>
                        <div className="text-xs text-gray-400 font-semibold">
                          {formatCurrency(actual2025.total_revenue, true)}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {actual2025.total_units || 0} units
                        </div>
                      </td>
                      
                      {/* Forecast Column - Stacked */}
                      <td className="px-3 py-2 text-right" style={{width: '140px'}}>
                        <div className="text-xs text-blue-400 font-semibold">
                          {formatCurrency(totals.forecastRevenue, true)}
                        </div>
                        <div className="text-[10px] text-blue-400/70">
                          {totals.forecastUnits} units
                        </div>
                      </td>
                      
                      {/* Actual Column - Stacked */}
                      <td className="px-3 py-2 text-right" style={{width: '140px'}}>
                        {hasActual2026 ? (
                          <>
                            <div className="text-xs text-white font-semibold">
                              {formatCurrency(totals.actualRevenue, true)}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {totals.actualUnits} units
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )}
                      </td>
                      
                      {/* Variance Column - Stacked */}
                      <td className="px-3 py-2 text-right" style={{width: '160px'}}>
                        {varianceRev !== null ? (
                          <>
                            <div className={`text-xs font-semibold flex items-center justify-end gap-1 ${
                              varianceRev > 0 ? 'text-green-400' : 
                              varianceRev < 0 ? 'text-red-400' : 'text-gray-400'
                            }`}>
                              {varianceRev > 0 && <TrendingUp className="w-3 h-3" />}
                              {varianceRev < 0 && <TrendingDown className="w-3 h-3" />}
                              {formatVariance(varianceRev, true)}
                            </div>
                            <div className={`text-[10px] ${
                              varianceUnits > 0 ? 'text-green-400/70' : 
                              varianceUnits < 0 ? 'text-red-400/70' : 'text-gray-500'
                            }`}>
                              {varianceUnits >= 0 ? '+' : ''}{varianceUnits} units
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                    
                    {/* Expanded Monthly View */}
                    {isExpanded && (
                      <tr>
                        <td colSpan="6" className="px-3 py-2 bg-slate-900/50">
                          {renderMonthlyTable(product)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredProducts.length > 50 && (
          <div className="p-3 text-center text-xs text-gray-400 border-t border-slate-700">
            Showing 50 of {filteredProducts.length} products. Use search to filter.
          </div>
        )}
      </div>
      
      {/* Unsaved Changes Warning Modal */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-slate-800 rounded-lg p-4 max-w-sm mx-4 shadow-xl border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Unsaved Changes</h4>
                <p className="text-xs text-gray-400 mb-4">
                  You have unsaved forecast changes. Do you want to discard them?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowUnsavedWarning(false);
                      setPendingCollapseSKU(null);
                    }}
                    className="px-3 py-1.5 text-xs text-gray-300 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  >
                    Keep Editing
                  </button>
                  <button
                    onClick={() => discardChanges(pendingCollapseSKU)}
                    className="px-3 py-1.5 text-xs text-white bg-red-600 hover:bg-red-500 rounded transition-colors"
                  >
                    Discard Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Manage View Component - Now with Dashboard and Grid Edit views
const ManageView = ({ forecast, onUpdate }) => {
  const [manageViewMode, setManageViewMode] = useState('dashboard'); // 'dashboard' or 'grid'
  
  return (
    <div>
      {/* View Mode Toggle */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex bg-slate-900 rounded-lg p-0.5">
          <button
            onClick={() => setManageViewMode('dashboard')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              manageViewMode === 'dashboard' 
                ? 'bg-slate-700 text-white' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Sliders className="w-3.5 h-3.5 inline mr-1" />
            Controls
          </button>
          <button
            onClick={() => setManageViewMode('grid')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              manageViewMode === 'grid' 
                ? 'bg-slate-700 text-white' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Edit3 className="w-3.5 h-3.5 inline mr-1" />
            Edit Grid View
          </button>
        </div>
      </div>

      {manageViewMode === 'dashboard' ? (
        <ControlsView forecast={forecast} onUpdate={onUpdate} />
      ) : (
        <GridEditView forecast={forecast} onUpdate={onUpdate} />
      )}
    </div>
  );
};

// Controls View - Forecast Management Tools
const ControlsView = ({ forecast, onUpdate }) => {
  const [generating, setGenerating] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [newTarget, setNewTarget] = useState(forecast?.summary?.annual_target || 300000);
  const [growthRate, setGrowthRate] = useState(1.20);
  const [message, setMessage] = useState(null);
  
  // Modal states
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  // Import VeeqoSyncModal dynamically to avoid circular dependencies
  const VeeqoSyncModal = React.lazy(() => import('../../components/Shared/VeeqoSyncModal'));

  // Function 1: Generate/Regenerate Forecast - show modal first
  const handleGenerateForecast = () => {
    setShowGenerateModal(true);
  };

  const confirmGenerateForecast = async () => {
    setShowGenerateModal(false);
    
    try {
      setGenerating(true);
      setMessage({ type: 'info', text: 'Generating forecast... This may take a moment.' });

      await api.post(`/api/v1/forecast/generate?year=2026&tenant_id=tenant-123&growth_rate=${growthRate}`);

      setMessage({ 
        type: 'success', 
        text: '✓ Forecast generated successfully!' 
      });
      
      setTimeout(() => {
        onUpdate();
      }, 1000);

    } catch (error) {
      console.error('Error generating forecast:', error);
      setMessage({ type: 'error', text: `Failed to generate forecast: ${error.message}` });
    } finally {
      setGenerating(false);
    }
  };

  // Function 2: Adjust Target - show modal first
  const handleAdjustTarget = () => {
    setShowAdjustModal(true);
  };

  const confirmAdjustTarget = async () => {
    setShowAdjustModal(false);
    
    try {
      setAdjusting(true);
      setMessage({ type: 'info', text: 'Adjusting forecast to new target...' });

      await api.post(`/api/v1/forecast/2026/adjust-target?tenant_id=tenant-123&new_target=${newTarget}`);

      setMessage({ 
        type: 'success', 
        text: `✓ Target adjusted to $${(newTarget / 1000).toFixed(0)}K! All products rescaled proportionally.` 
      });
      
      setTimeout(() => {
        onUpdate();
      }, 1000);

    } catch (error) {
      console.error('Error adjusting target:', error);
      setMessage({ type: 'error', text: `Failed to adjust target: ${error.message}` });
    } finally {
      setAdjusting(false);
    }
  };

  // Function 3: Export to CSV
  const handleExportCSV = () => {
    if (!forecast || !forecast.products) {
      setMessage({ type: 'error', text: 'No data to export' });
      return;
    }

    let csv = 'SKU,Product,2025 Actual ($),2026 Forecast ($),2026 Actual ($),Variance ($)\n';
    
    forecast.products.forEach(p => {
      const actual2025 = p.actual_2025?.total_revenue || 0;
      const forecast2026 = p.forecast_2026?.total_revenue || 0;
      const actual2026 = p.actual_2026?.total_revenue || 0;
      const variance = actual2026 - forecast2026;
      const title = (p.product_title || '').replace(/,/g, ' ').replace(/"/g, "'");
      csv += `${p.sku},"${title}",${actual2025.toFixed(2)},${forecast2026.toFixed(2)},${actual2026.toFixed(2)},${variance.toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forecast-2026-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setMessage({ type: 'success', text: '✓ Forecast exported to CSV!' });
  };

  const currentTarget = forecast?.summary?.annual_target || 0;
  const changePercent = currentTarget > 0 ? ((newTarget / currentTarget - 1) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Message Banner */}
      {message && (
        <div className={`p-2 rounded-lg border ${
          message.type === 'success' ? 'bg-green-900/20 border-green-600/50 text-green-400' :
          message.type === 'error' ? 'bg-red-900/20 border-red-600/50 text-red-400' :
          'bg-blue-900/20 border-blue-600/50 text-blue-400'
        }`}>
          <p className="text-xs">{message.text}</p>
        </div>
      )}

      {/* Section 1: Generate Forecast */}
      <div className="bg-slate-800 rounded-lg p-3">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-blue-400" />
          Regenerate Forecast
        </h3>
        <p className="text-[10px] text-gray-400 mb-3">
          Generate new forecast from 2025 Veeqo order data. This will overwrite the current forecast.
        </p>
        
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-[10px] uppercase text-gray-400 mb-1 block">Growth Rate</label>
            <input
              type="number"
              step="0.01"
              min="1"
              max="2"
              value={growthRate}
              onChange={(e) => setGrowthRate(parseFloat(e.target.value) || 1.20)}
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
            />
            <p className="text-[10px] text-gray-500 mt-0.5">{((growthRate - 1) * 100).toFixed(0)}% growth</p>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={handleGenerateForecast}
              disabled={generating}
              className="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded text-xs font-semibold flex items-center justify-center gap-1"
            >
              {generating ? (
                <><RefreshCw className="w-3 h-3 animate-spin" /> Generating...</>
              ) : (
                <><RefreshCw className="w-3 h-3" /> Generate Forecast</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Section 2: Adjust Target */}
      <div className="bg-slate-800 rounded-lg p-3">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <Target className="w-4 h-4 text-yellow-400" />
          Adjust Target
        </h3>
        <p className="text-[10px] text-gray-400 mb-3">
          Change the 2026 target and rescale all product forecasts proportionally.
        </p>
        
        <div className="mb-3">
          <label className="text-[10px] uppercase text-gray-400 mb-1 block">New Annual Target ($)</label>
          <div className="flex gap-2">
            <input
              type="number"
              value={newTarget}
              onChange={(e) => setNewTarget(parseInt(e.target.value) || 0)}
              className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white font-bold"
            />
            <button
              onClick={handleAdjustTarget}
              disabled={adjusting || newTarget === currentTarget}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 text-white rounded text-xs font-semibold"
            >
              {adjusting ? 'Adjusting...' : 'Apply'}
            </button>
          </div>
          
          {currentTarget > 0 && (
            <p className="text-[10px] text-gray-400 mt-1">
              Current: ${(currentTarget / 1000).toFixed(1)}K → 
              New: ${(newTarget / 1000).toFixed(1)}K 
              <span className={changePercent >= 0 ? 'text-green-400' : 'text-red-400'}>
                ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%)
              </span>
            </p>
          )}
        </div>
        
        <div className="bg-yellow-900/20 border border-yellow-600/30 rounded p-2">
          <p className="text-[10px] text-yellow-400">
            ⚠️ This will proportionally adjust ALL {forecast?.summary?.total_products || 0} product forecasts to hit the new target.
          </p>
        </div>
      </div>

      {/* Section 3: Export & Tools */}
      <div className="bg-slate-800 rounded-lg p-3">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple-400" />
          Export & Tools
        </h3>
        
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleExportCSV}
            className="px-3 py-2 bg-slate-900 border border-slate-700 hover:bg-slate-700 text-blue-400 rounded text-xs flex items-center justify-center gap-1"
          >
            <Download className="w-3 h-3" />
            Export to CSV
          </button>
          
          <button
            onClick={() => window.print()}
            className="px-3 py-2 bg-slate-900 border border-slate-700 hover:bg-slate-700 text-blue-400 rounded text-xs flex items-center justify-center gap-1"
          >
            <Printer className="w-3 h-3" />
            Print Report
          </button>
          
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setMessage({ type: 'success', text: 'Link copied!' });
            }}
            className="px-3 py-2 bg-slate-900 border border-slate-700 hover:bg-slate-700 text-gray-400 rounded text-xs col-span-2 flex items-center justify-center gap-1"
          >
            <Link className="w-3 h-3" />
            Copy Link
          </button>
        </div>
      </div>

      {/* Section 4: Sync 2025 Data */}
      <div className="bg-slate-800 rounded-lg p-3 border-2 border-blue-600/30">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-cyan-400" />
          Sync 2025 Actuals
        </h3>
        <p className="text-[10px] text-gray-400 mb-3">
          Update forecast baseline with latest Veeqo order data. Sync before regenerating forecast.
        </p>
        
        <button
          onClick={() => setShowSyncModal(true)}
          className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Sync Veeqo Orders
        </button>
      </div>

      {/* Veeqo Sync Modal */}
      <React.Suspense fallback={null}>
        {showSyncModal && (
          <VeeqoSyncModal
            isOpen={showSyncModal}
            onClose={() => setShowSyncModal(false)}
            onSyncStarted={() => {
              // After sync, suggest regenerating forecast
              setMessage({ 
                type: 'success', 
                text: 'Sync complete! You can now regenerate the forecast with updated 2025 data.' 
              });
            }}
          />
        )}
      </React.Suspense>

      {/* Section 5: Current Forecast Info */}
      {forecast && forecast.summary && (
        <div className="bg-slate-800 rounded-lg p-3">
          <h3 className="text-xs font-semibold text-white mb-2">ℹ️ Current Forecast Info</h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-400">Generated:</span>
              <span className="text-white">{forecast.generated_at ? new Date(forecast.generated_at).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Products:</span>
              <span className="text-white">{forecast.summary.total_products} SKUs</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Growth Rate:</span>
              <span className="text-white">{((forecast.growth_rate - 1) * 100).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">2026 Target:</span>
              <span className="text-white">${(forecast.summary.annual_target / 1000).toFixed(1)}K</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">YTD Actual:</span>
              <span className="text-green-400">${(forecast.summary.ytd_actual / 1000).toFixed(1)}K</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Progress:</span>
              <span className="text-blue-400">{forecast.summary.annual_target > 0 ? ((forecast.summary.ytd_actual / forecast.summary.annual_target) * 100).toFixed(1) : 0}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Generate Forecast Confirmation Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-4 max-w-md border border-slate-700 mx-4">
            <h3 className="text-sm font-bold text-white mb-3">⚠️ Regenerate Forecast</h3>
            
            <div className="space-y-2 mb-4 text-xs text-gray-300">
              <p>This will:</p>
              <ul className="list-disc list-inside space-y-1 text-[10px] ml-2">
                <li>Fetch all 2025 sales data from Veeqo orders</li>
                <li>Calculate new forecast with <span className="text-blue-400 font-semibold">{((growthRate - 1) * 100).toFixed(0)}%</span> growth rate</li>
                <li>Generate monthly forecasts for all SKUs</li>
                <li className="text-yellow-400 font-semibold">Overwrite current forecast data</li>
              </ul>
              
              {forecast && (
                <div className="bg-slate-900 rounded p-2 mt-3">
                  <p className="text-[10px] text-gray-400 mb-1">Current Forecast:</p>
                  <div className="grid grid-cols-2 gap-1 text-[10px]">
                    <span className="text-gray-400">Products:</span>
                    <span className="text-white">{forecast.products?.length || 0} SKUs</span>
                    <span className="text-gray-400">Target:</span>
                    <span className="text-white">${(forecast.summary?.annual_target / 1000).toFixed(1)}K</span>
                    <span className="text-gray-400">Generated:</span>
                    <span className="text-white">{forecast.generated_at ? new Date(forecast.generated_at).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              )}
              
              <p className="text-yellow-400 text-[10px] mt-2 p-2 bg-yellow-900/20 rounded">
                ⚠️ This action cannot be undone. The current forecast will be replaced.
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs"
              >
                Cancel
              </button>
              <button
                onClick={confirmGenerateForecast}
                className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold"
              >
                🔄 Generate Forecast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Target Confirmation Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-4 max-w-md border border-slate-700 mx-4">
            <h3 className="text-sm font-bold text-white mb-3">🎯 Adjust Target</h3>
            
            <div className="space-y-2 mb-4 text-xs text-gray-300">
              <p>You are about to adjust the 2026 target:</p>
              
              <div className="bg-slate-900 rounded p-3 my-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-xs">Current Target:</span>
                  <span className="text-white font-bold">${(currentTarget / 1000).toFixed(1)}K</span>
                </div>
                <div className="flex items-center justify-center my-2">
                  <span className="text-2xl text-blue-400">↓</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs">New Target:</span>
                  <span className="text-green-400 font-bold text-lg">${(newTarget / 1000).toFixed(1)}K</span>
                </div>
              </div>
              
              {forecast && (
                <>
                  <div className="bg-blue-900/20 border border-blue-600/30 rounded p-2">
                    <p className="text-[10px] text-blue-300">
                      <strong>Change:</strong> {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%
                      {changePercent > 0 ? ' increase' : changePercent < 0 ? ' decrease' : ''}
                    </p>
                    <p className="text-[10px] text-blue-300 mt-1">
                      <strong>Impact:</strong> All {forecast.products?.length || 0} product forecasts will be scaled proportionally
                    </p>
                  </div>
                  
                  <div className="bg-yellow-900/20 border border-yellow-600/30 rounded p-2 mt-2">
                    <p className="text-[10px] text-yellow-300">
                      ⚠️ Example: If SKU forecast is $1.5K, it will become ${((1500 * newTarget / currentTarget) / 1000).toFixed(2)}K
                    </p>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowAdjustModal(false)}
                className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs"
              >
                Cancel
              </button>
              <button
                onClick={confirmAdjustTarget}
                className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-semibold"
              >
                🎯 Adjust Target
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Grid Edit View Component - Spreadsheet-like editing with COGS & Profit
const GridEditView = ({ forecast, onUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('forecast_2026');
  const [sortDirection, setSortDirection] = useState('desc');
  const [editedProducts, setEditedProducts] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Calculate derived data
  const products = forecast?.products || [];
  
  // Helper to get edited or original values
  const getProductValues = useCallback((product) => {
    const edited = editedProducts[product.sku] || {};
    const forecast2026 = product.forecast_2026 || {};
    const pm = product.product_master || {};
    
    const units = edited.units ?? forecast2026.total_units ?? 0;
    const price = edited.price ?? forecast2026.avg_price ?? pm.current_price ?? 0;
    const cogsPerUnit = forecast2026.cogs_per_unit ?? pm.cogs ?? 0;
    
    const revenue = units * price;
    const cogs = units * cogsPerUnit;
    const profit = revenue - cogs;
    const marginPct = revenue > 0 ? (profit / revenue) * 100 : 0;
    
    return { units, price, cogsPerUnit, revenue, cogs, profit, marginPct };
  }, [editedProducts]);
  
  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = products.filter(p => 
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.product_title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      const aVals = getProductValues(a);
      const bVals = getProductValues(b);
      
      switch (sortField) {
        case 'sku':
          aVal = a.sku || '';
          bVal = b.sku || '';
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'actual_2025':
          aVal = a.actual_2025?.total_revenue || 0;
          bVal = b.actual_2025?.total_revenue || 0;
          break;
        case 'forecast_2026':
          aVal = aVals.revenue;
          bVal = bVals.revenue;
          break;
        case 'profit':
          aVal = aVals.profit;
          bVal = bVals.profit;
          break;
        case 'margin':
          aVal = aVals.marginPct;
          bVal = bVals.marginPct;
          break;
        case 'actual_2026':
          aVal = a.actual_2026?.total_revenue || 0;
          bVal = b.actual_2026?.total_revenue || 0;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [products, searchTerm, sortField, sortDirection, getProductValues]);

  // Calculate totals with COGS and profit
  const totals = useMemo(() => {
    let totalActual2025 = 0;
    let totalUnits2025 = 0;
    let totalForecastRevenue = 0;
    let totalForecastUnits = 0;
    let totalForecastCogs = 0;
    let totalForecastProfit = 0;
    let totalActual2026 = 0;
    
    filteredProducts.forEach(p => {
      totalActual2025 += p.actual_2025?.total_revenue || 0;
      totalUnits2025 += p.actual_2025?.total_units || 0;
      totalActual2026 += p.actual_2026?.total_revenue || 0;
      
      const vals = getProductValues(p);
      totalForecastRevenue += vals.revenue;
      totalForecastUnits += vals.units;
      totalForecastCogs += vals.cogs;
      totalForecastProfit += vals.profit;
    });
    
    const avgMargin = totalForecastRevenue > 0 ? (totalForecastProfit / totalForecastRevenue) * 100 : 0;
    
    return { 
      totalActual2025, 
      totalUnits2025,
      totalForecastRevenue, 
      totalForecastUnits,
      totalForecastCogs,
      totalForecastProfit,
      avgMargin,
      totalActual2026 
    };
  }, [filteredProducts, getProductValues]);

  const hasChanges = Object.keys(editedProducts).length > 0;
  const editedCount = Object.keys(editedProducts).length;

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle cell edit - units (with focus preservation)
  const handleUnitsChange = useCallback((sku, value) => {
    const numValue = parseInt(value) || 0;
    
    setEditedProducts(prev => {
      const existing = prev[sku] || {};
      return { 
        ...prev, 
        [sku]: { ...existing, sku, units: numValue }
      };
    });
  }, []);

  // Handle cell edit - price (with focus preservation)
  const handlePriceChange = useCallback((sku, value) => {
    const numValue = parseFloat(value) || 0;
    
    setEditedProducts(prev => {
      const existing = prev[sku] || {};
      return { 
        ...prev, 
        [sku]: { ...existing, sku, price: numValue }
      };
    });
  }, []);

  // Handle input focus - select all and prevent auto-scroll
  const handleInputFocus = useCallback((e) => {
    e.target.select();
  }, []);

  // Discard changes
  const handleDiscard = () => {
    setEditedProducts({});
    setMessage({ type: 'info', text: 'Changes discarded' });
    setTimeout(() => setMessage(null), 2000);
  };

  // Save all changes - improved error handling
  const handleSaveAll = async () => {
    if (!hasChanges) return;
    
    try {
      setSaving(true);
      setMessage({ type: 'info', text: `Saving ${editedCount} changes...` });
      
      // Prepare bulk update payload - ensure proper format
      const updates = Object.entries(editedProducts).map(([sku, edit]) => ({
        sku: sku,
        new_units: edit.units !== undefined ? edit.units : null,
        new_price: edit.price !== undefined ? edit.price : null
      }));
      
      console.log('Saving updates:', updates); // Debug log
      
      const result = await api.post('/api/v1/forecast/2026/bulk-update?tenant_id=tenant-123', {
        updates
      });
      
      console.log('Save result:', result); // Debug log
      
      const profitMsg = result.total_profit ? ` | Profit: $${(result.total_profit / 1000).toFixed(1)}K` : '';
      setMessage({ type: 'success', text: `✓ Saved ${editedCount} changes!${profitMsg}` });
      setEditedProducts({});
      
      // Refresh data
      setTimeout(() => {
        onUpdate();
      }, 500);
      
    } catch (error) {
      console.error('Error saving changes:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      setMessage({ type: 'error', text: `Failed to save: ${errorMsg}` });
    } finally {
      setSaving(false);
    }
  };

  // Export grid to CSV with COGS and profit
  const handleExportGrid = () => {
    let csv = 'SKU,Product,2025 Units,2025 Revenue,2026 Units,2026 Price,2026 Revenue,COGS,Profit,Margin %,2026 Actual,Status\n';
    
    filteredProducts.forEach(p => {
      const vals = getProductValues(p);
      const actual2026 = p.actual_2026?.total_revenue || 0;
      const status = editedProducts[p.sku] ? 'Edited' : 'Original';
      const title = (p.product_title || '').replace(/,/g, ' ').replace(/"/g, "'");
      csv += `${p.sku},"${title}",${p.actual_2025?.total_units || 0},${(p.actual_2025?.total_revenue || 0).toFixed(2)},${vals.units},${vals.price.toFixed(2)},${vals.revenue.toFixed(2)},${vals.cogs.toFixed(2)},${vals.profit.toFixed(2)},${vals.marginPct.toFixed(1)},${actual2026.toFixed(2)},${status}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forecast-grid-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setMessage({ type: 'success', text: '✓ Grid exported to CSV!' });
  };

  // Margin color helper
  const getMarginColor = (margin) => {
    if (margin >= 50) return 'text-green-400';
    if (margin >= 30) return 'text-yellow-400';
    if (margin >= 0) return 'text-orange-400';
    return 'text-red-400';
  };

  // Sort icon helper
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header Bar */}
      <div className="bg-slate-800 rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Left: Search and Count */}
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search SKU or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 w-56"
            />
            <span className="text-xs text-gray-400">
              Showing {filteredProducts.length} of {products.length} products
            </span>
          </div>
          
          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Unsaved changes warning */}
            {hasChanges && (
              <div className="flex items-center gap-2 text-yellow-400 text-xs bg-yellow-900/20 px-3 py-1.5 rounded">
                <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                {editedCount} unsaved change{editedCount !== 1 ? 's' : ''}
              </div>
            )}
            
            <button
              onClick={handleExportGrid}
              className="px-3 py-1.5 bg-slate-900 border border-slate-700 hover:bg-slate-700 text-gray-300 rounded text-xs flex items-center gap-1"
            >
              <Download className="w-3 h-3" />
              Export
            </button>
            
            <button
              onClick={handleDiscard}
              disabled={!hasChanges || saving}
              className="px-3 py-1.5 bg-slate-900 border border-slate-700 hover:bg-slate-700 text-gray-300 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Discard
            </button>
            
            <button
              onClick={handleSaveAll}
              disabled={!hasChanges || saving}
              className={`px-4 py-1.5 rounded text-xs font-semibold flex items-center gap-1 ${
                hasChanges 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-slate-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <><Clock className="w-3 h-3 animate-pulse" /> Saving...</>
              ) : (
                <><Save className="w-3 h-3" /> Save All</>
              )}
            </button>
          </div>
        </div>
        
        {/* Profit Summary Bar */}
        <div className="flex items-center gap-6 mt-3 pt-3 border-t border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Revenue:</span>
            <span className="text-sm text-blue-400 font-semibold">${(totals.totalForecastRevenue / 1000).toFixed(1)}K</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">COGS:</span>
            <span className="text-sm text-orange-400 font-semibold">${(totals.totalForecastCogs / 1000).toFixed(1)}K</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Profit:</span>
            <span className="text-sm text-green-400 font-semibold">${(totals.totalForecastProfit / 1000).toFixed(1)}K</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Margin:</span>
            <span className={`text-sm font-semibold ${getMarginColor(totals.avgMargin)}`}>{totals.avgMargin.toFixed(1)}%</span>
          </div>
        </div>
        
        {/* Message Banner */}
        {message && (
          <div className={`mt-3 p-2 rounded text-xs ${
            message.type === 'success' ? 'bg-green-900/20 text-green-400' :
            message.type === 'error' ? 'bg-red-900/20 text-red-400' :
            'bg-blue-900/20 text-blue-400'
          }`}>
            {message.text}
          </div>
        )}
      </div>

      {/* Grid Table */}
      <div className="bg-slate-800 rounded-lg overflow-hidden flex-1 flex flex-col">
        <div className="overflow-x-auto flex-1">
          <table className="w-full min-w-[1600px] table-fixed">
            {/* Column widths - wider for better readability */}
            <colgroup>
              <col style={{width: '110px'}} />    {/* SKU */}
              <col style={{width: '65px'}} />     {/* 2025 Units */}
              <col style={{width: '75px'}} />     {/* 2025 Price */}
              <col style={{width: '85px'}} />     {/* 2025 Rev */}
              <col style={{width: '85px'}} />     {/* 2026 Units */}
              <col style={{width: '95px'}} />     {/* 2026 Price */}
              <col style={{width: '70px'}} />     {/* COGS */}
              <col style={{width: '85px'}} />     {/* Profit */}
              <col style={{width: '70px'}} />     {/* Margin% */}
              <col style={{width: '100px'}} />    {/* 2026 ACT */}
              <col style={{width: '70px'}} />     {/* STATUS */}
              <col style={{minWidth: '300px'}} /> {/* PRODUCT - minimum width, expands */}
            </colgroup>
            <thead className="bg-slate-900 sticky top-0 z-20">
              {/* ROW 1: Main section headers */}
              <tr>
                <th rowSpan="2" className="px-3 py-3 text-left text-xs font-semibold uppercase text-gray-400 sticky left-0 bg-slate-900 z-10 border-r border-slate-600">
                  SKU
                </th>
                
                {/* 2025 ACTUAL - 3 columns */}
                <th colSpan="3" className="px-3 py-3 text-center text-xs font-semibold uppercase text-gray-500 bg-slate-800 border-b border-slate-600 border-r border-slate-600">
                  2025 ACTUAL
                </th>
                
                {/* 2026 FORECAST - 5 columns */}
                <th colSpan="5" className="px-3 py-3 text-center text-xs font-bold uppercase text-blue-400 bg-blue-900/40 border-b border-slate-600 border-r border-slate-600 border-l-2 border-l-blue-500/50">
                  2026 FORECAST
                </th>
                
                <th rowSpan="2" className="px-3 py-3 text-center text-xs font-semibold uppercase text-gray-400 border-r border-slate-600">
                  2026 ACT
                </th>
                
                <th rowSpan="2" className="px-3 py-3 text-center text-xs font-semibold uppercase text-gray-400 border-r border-slate-600">
                  STATUS
                </th>
                
                {/* PRODUCT moved to end */}
                <th rowSpan="2" className="px-3 py-3 text-left text-xs font-semibold uppercase text-gray-400 bg-slate-900 border-l border-slate-600">
                  PRODUCT
                </th>
              </tr>
              
              {/* ROW 2: Sub-column headers */}
              <tr className="border-b border-slate-600">
                {/* 2025 ACTUAL sub-headers (3 columns) */}
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 bg-slate-800">Units</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 bg-slate-800">Price</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 bg-slate-800 border-r border-slate-600">Rev</th>
                
                {/* 2026 FORECAST sub-headers (5 columns) */}
                <th className="px-3 py-2 text-right text-[11px] uppercase text-blue-400 bg-blue-900/40 border-l-2 border-l-blue-500/50">Units</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-blue-400 bg-blue-900/40">Price</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-orange-400 bg-slate-800">COGS</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-green-400 bg-slate-800">Profit</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-400 bg-slate-800 border-r border-slate-600">Mar%</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product, index) => {
                const isEdited = !!editedProducts[product.sku];
                const vals = getProductValues(product);
                const actual2025 = product.actual_2025 || {};
                const actual2026Revenue = product.actual_2026?.total_revenue || 0;
                const actual2026Units = product.actual_2026?.total_units || 0;
                const hasActual2026 = actual2026Revenue > 0;
                const pmPrice = product.product_master?.current_price || 0;
                const pmCogs = product.product_master?.cogs || 0;
                const avg2025Price = actual2025.avg_price || 0;
                
                // Calculate achievement percentage
                const achievementPct = vals.revenue > 0 && actual2026Revenue > 0 
                  ? (actual2026Revenue / vals.revenue) * 100 
                  : 0;
                
                // Alternating row colors
                const rowBg = index % 2 === 0 ? 'bg-slate-800/50' : 'bg-slate-800/80';
                
                return (
                  <tr 
                    key={product.sku} 
                    className={`border-b border-slate-700/50 hover:bg-slate-700/40 ${rowBg} ${isEdited ? 'ring-1 ring-inset ring-blue-500/30' : ''}`}
                  >
                    {/* 1. SKU (sticky) + edit indicator */}
                    <td className={`px-3 py-2 text-sm text-white font-mono sticky left-0 z-10 border-r border-slate-700/50 ${index % 2 === 0 ? 'bg-slate-800/50' : 'bg-slate-800/80'}`} title={product.sku}>
                      {product.sku}
                      {isEdited && <span className="ml-1 text-yellow-400 text-xs">●</span>}
                    </td>
                    
                    {/* === 2025 ACTUAL (3 columns) === */}
                    <td className="px-3 py-2 text-sm text-gray-500 text-right">
                      {actual2025.total_units || 0}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500 text-right">
                      ${avg2025Price.toFixed(0)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500 text-right border-r border-slate-700/50">
                      ${((actual2025.total_revenue || 0) / 1000).toFixed(1)}K
                    </td>
                    
                    {/* === 2026 FORECAST (5 columns) === */}
                    {/* Units (EDITABLE) */}
                    <td className="px-1.5 py-1.5 bg-blue-900/20 border-l-2 border-l-blue-500/30">
                      <input
                        type="number"
                        value={vals.units}
                        onChange={(e) => handleUnitsChange(product.sku, e.target.value)}
                        onFocus={handleInputFocus}
                        autoComplete="off"
                        tabIndex={0}
                        className={`w-full bg-slate-900/80 border rounded px-2 py-1.5 text-sm text-white text-right focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${
                          isEdited && editedProducts[product.sku]?.units !== undefined
                            ? 'border-blue-500' 
                            : 'border-slate-600/50'
                        }`}
                      />
                    </td>
                    
                    {/* Price (EDITABLE) */}
                    <td className="px-1.5 py-1.5 bg-blue-900/20 relative group">
                      <input
                        type="number"
                        step="0.01"
                        value={vals.price.toFixed(2)}
                        onChange={(e) => handlePriceChange(product.sku, e.target.value)}
                        onFocus={handleInputFocus}
                        autoComplete="off"
                        tabIndex={0}
                        className={`w-full bg-slate-900/80 border rounded px-2 py-1.5 text-sm text-white text-right focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${
                          isEdited && editedProducts[product.sku]?.price !== undefined
                            ? 'border-blue-500' 
                            : 'border-slate-600/50'
                        }`}
                      />
                      {/* Product Master tooltip */}
                      {(pmPrice > 0 || pmCogs > 0) && (
                        <div className="hidden group-hover:block absolute z-30 bg-slate-900 border border-slate-500 rounded p-2 text-xs whitespace-nowrap -top-12 left-0 shadow-xl">
                          <p className="text-gray-400">PM: ${pmPrice.toFixed(0)} | COG: ${pmCogs.toFixed(0)}</p>
                        </div>
                      )}
                    </td>
                    
                    {/* COGS (AUTO) */}
                    <td className="px-3 py-2 text-sm text-orange-400 text-right" title={`$${vals.cogsPerUnit.toFixed(2)}/unit`}>
                      ${vals.cogsPerUnit.toFixed(0)}
                    </td>
                    
                    {/* Profit (AUTO) */}
                    <td className={`px-3 py-2 text-sm text-right font-semibold ${vals.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${(vals.profit / 1000).toFixed(1)}K
                    </td>
                    
                    {/* Margin% (AUTO) */}
                    <td className={`px-3 py-2 text-sm text-right border-r border-slate-700/50 ${getMarginColor(vals.marginPct)}`}>
                      {vals.marginPct.toFixed(0)}%
                    </td>
                    
                    {/* 2026 ACTUAL */}
                    <td className="px-3 py-2 text-sm text-gray-300 text-right border-r border-slate-700/50">
                      {hasActual2026 
                        ? `${actual2026Units}·$${(actual2026Revenue / 1000).toFixed(1)}K`
                        : '-'}
                    </td>
                    
                    {/* STATUS */}
                    <td className="px-3 py-2 text-center border-r border-slate-700/50">
                      {hasActual2026 ? (
                        <span className={`text-sm font-bold ${
                          achievementPct >= 100 ? 'text-green-400' :
                          achievementPct >= 75 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {achievementPct.toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600">-</span>
                      )}
                    </td>
                    
                    {/* PRODUCT (moved to end) - shortened name for readability */}
                    <td className="px-3 py-2 text-sm text-gray-300 border-l border-slate-600" title={product.product_title}>
                      <div className="line-clamp-1">
                        {getShortProductName(product.product_title)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Sticky Footer - Totals */}
        <div className="bg-slate-900 border-t-2 border-slate-600 sticky bottom-0 z-20">
          <table className="w-full min-w-[1600px] table-fixed">
            <colgroup>
              <col style={{width: '110px'}} />    {/* SKU */}
              <col style={{width: '65px'}} />     {/* 2025 Units */}
              <col style={{width: '75px'}} />     {/* 2025 Price */}
              <col style={{width: '85px'}} />     {/* 2025 Rev */}
              <col style={{width: '85px'}} />     {/* 2026 Units */}
              <col style={{width: '95px'}} />     {/* 2026 Price */}
              <col style={{width: '70px'}} />     {/* COGS */}
              <col style={{width: '85px'}} />     {/* Profit */}
              <col style={{width: '70px'}} />     {/* Margin% */}
              <col style={{width: '100px'}} />    {/* 2026 ACT */}
              <col style={{width: '70px'}} />     {/* STATUS */}
              <col style={{minWidth: '300px'}} /> {/* PRODUCT */}
            </colgroup>
            <tfoot>
              <tr className="font-semibold text-sm">
                {/* SKU - sticky */}
                <td className="px-3 py-3 text-white sticky left-0 bg-slate-900 z-10 border-r border-slate-600">
                  TOTAL
                </td>
                
                {/* 2025 ACTUAL (3 columns) */}
                <td className="px-3 py-3 text-gray-500 text-right">
                  {totals.totalUnits2025}
                </td>
                <td className="px-3 py-3 text-gray-500 text-right">
                  -
                </td>
                <td className="px-3 py-3 text-gray-500 text-right border-r border-slate-600">
                  ${(totals.totalActual2025 / 1000).toFixed(1)}K
                </td>
                
                {/* 2026 FORECAST (5 columns) */}
                <td className="px-3 py-3 text-blue-400 text-right bg-blue-900/30 border-l-2 border-l-blue-500/50">
                  {totals.totalForecastUnits}
                </td>
                <td className="px-3 py-3 text-blue-400 text-right bg-blue-900/30">
                  -
                </td>
                <td className="px-3 py-3 text-orange-400 text-right">
                  ${(totals.totalForecastCogs / 1000).toFixed(1)}K
                </td>
                <td className="px-3 py-3 text-green-400 text-right font-bold">
                  ${(totals.totalForecastProfit / 1000).toFixed(1)}K
                </td>
                <td className={`px-3 py-3 text-right font-bold border-r border-slate-600 ${getMarginColor(totals.avgMargin)}`}>
                  {totals.avgMargin.toFixed(0)}%
                </td>
                
                {/* 2026 ACTUAL */}
                <td className="px-3 py-3 text-gray-300 text-right border-r border-slate-600">
                  ${(totals.totalActual2026 / 1000).toFixed(1)}K
                </td>
                
                {/* STATUS */}
                <td className="px-3 py-3 text-center text-gray-600 border-r border-slate-600">
                  -
                </td>
                
                {/* PRODUCT */}
                <td className="px-3 py-3 text-gray-400 border-l border-slate-600">
                  ({filteredProducts.length} products)
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ForecastTab;
